import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '../services/firebase';
import { VideoJob } from '../types';

/**
 * Hook to monitor a single video job in real-time
 * Automatically subscribes to Firestore updates for the job
 *
 * @param jobId - The ID of the job to monitor (can be null)
 * @returns {object} - { job, loading, error }
 */
export function useVideoJob(jobId: string | null) {
  const [job, setJob] = useState<VideoJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      doc(db, 'jobs', jobId),
      (snapshot) => {
        if (snapshot.exists()) {
          setJob({ id: snapshot.id, ...snapshot.data() } as VideoJob);
        } else {
          setJob(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error watching job:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [jobId]);

  return { job, loading, error };
}
